import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AdjacentType, BranchingQueryDefinition, Gateway, GatewayClassName, GatewayConfig, getZombieInstanceFromPrimaryKey, NodeEdgeMapper, toTitleCase, } from '@iotv/datamodel-core';
import { isVOBofType, Networks } from '@iotv/iotv-v3-types';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { useMostProximateCustomer } from '../../hooks/useMostProximateCustomer';
import { useTabHandlerStringKeys } from '../../hooks/useTabHandler';
import { ObjectHeader } from '../../v2/components/generic/ObjectView/ObjectHeader';
import { IncidentListCard } from '../common/IncidentListCard';
import { commonListViewDefinition, getListViewDefinition } from '../factories/AttributeDefinitions';
import { Listerlizer } from '../factories/Listerlizer/Listerlizer';
import { CustomerPropertyAreaPlaceQuery } from '../NetworkCoverage/QueryPaths/customerPropertyAreaPlace';
import { GatewayConfigContext } from '../SytemManagement/Gateway/Context/gatewayContext';
import DetailsTab from './Tabs/DetailsTab';
import GatewayAssociationTab from './Tabs/GatewayAssociationTab';
import { GatewayCertificatesTab } from './Tabs/GatewayCertificatesTab';
import GatewayConfigurationTab from './Tabs/GatewayConfigurationTab';
import GatewayRecordsTab from './Tabs/GatewayRecordsTab';
import LinksTab from './Tabs/LinksTab';
import NetworkTab from './Tabs/NetworkTab';
const GatewayForm = (props) => {
    const { viewObject, params, contextCustomer, user, userGroupRoles, userSelectedGroup, roleArbitrationInstance, applicationData, userFunctions, transactionFunctions, isSmallView, } = props;
    const matchedPrimary = viewObject.matchedPrimary;
    const id = params.id;
    const { state: { gateway, gatewayConfig }, dispatch, } = useContext(GatewayConfigContext);
    const mostProximateCustomer = useMostProximateCustomer({ item: matchedPrimary });
    const [mapper, setMapper] = useState();
    useEffect(() => {
        const getMapper = async (gateway) => {
            const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
                contextObjects: [gateway],
                query: new BranchingQueryDefinition({
                    id: gateway.sk,
                    path: [{ objectTypeId: GatewayClassName, adjacencyType: AdjacentType.SUBJECT }],
                    subQueries: [
                        new BranchingQueryDefinition({
                            id: `${gateway.sk}_Group_Customer`,
                            path: [
                                { objectTypeId: 'Group', adjacencyType: AdjacentType.PARENT },
                                { objectTypeId: 'Customer', adjacencyType: AdjacentType.PARENT },
                            ],
                        }),
                        new BranchingQueryDefinition({
                            id: `${gateway.sk}_Property`,
                            path: [{ objectTypeId: 'Property', adjacencyType: AdjacentType.PARENT }],
                        }),
                        new BranchingQueryDefinition({
                            id: `${gateway.sk}_Place`,
                            path: [{ objectTypeId: 'Place', adjacencyType: AdjacentType.PARENT }],
                        }),
                    ],
                }),
            });
            if (!err && data) {
                userFunctions.addObjectsToState(data.getContainedNodesAndEdges());
                setMapper(data);
            }
            else {
                setMapper(new NodeEdgeMapper());
            }
        };
        if (gateway)
            getMapper(gateway);
    }, [gateway?.sk]);
    useEffect(() => {
        if (!id)
            return;
        const zombieGateway = getZombieInstanceFromPrimaryKey(`${GatewayClassName}:${id.toUpperCase()}`);
        transactionFunctions.getObject(zombieGateway).then((data) => {
            if (isVOBofType(GatewayClassName, data))
                dispatch({ gateway: new Gateway(data) });
        });
    }, [id]);
    useEffect(() => {
        const getGatewayConfig = async (gateway) => {
            const { err, data: gatewayConfigData } = await ConsumerDao.getExact(GatewayConfig.getGatewayConfigKeys(gateway));
            const vob = gatewayConfigData?.Items?.[0];
            if (vob) {
                const gatewayConfig = new GatewayConfig(vob);
                userFunctions.addObjectsToState([gatewayConfig]);
                dispatch({ gatewayConfig });
            }
        };
        if (gateway && gateway?.networkKey === Networks.AWS_LPWAN)
            getGatewayConfig(gateway);
    }, [gateway?.sk]);
    const tabsToDisplay = {
        Details: gateway && _jsx(DetailsTab, { contextObject: undefined, ...props, gateway, mostProximateCustomer, mapper }),
        Links: gateway && _jsx(LinksTab, { mapper }),
        Places: gateway && (_jsx(Listerlizer, { viewObject: { ...viewObject, matchedPrimary: gateway }, adjacentFilter: { objectType: 'Place', adjacentType: AdjacentType.PARENT }, viewDefinition: getListViewDefinition('Place') ?? commonListViewDefinition, contextObject: gateway, searchRootObjects: [contextCustomer], linkableItemsBranchingQueryDefinition: CustomerPropertyAreaPlaceQuery, applicationData: applicationData, userFunctions: userFunctions, transactionFunctions: transactionFunctions })),
        Configuration: gateway && _jsx(GatewayConfigurationTab, { contextObject: undefined, ...props, gateway, mostProximateCustomer }),
        Associations: gateway && _jsx(GatewayAssociationTab, { contextObject: undefined, ...props, gateway }),
        Records: gateway && _jsx(GatewayRecordsTab, { gateway: gateway }),
        Network: gateway && _jsx(NetworkTab, { gateway: gateway }),
        Incidents: gateway && (_jsx(IncidentListCard, { viewObject, contextCustomer, user, userGroupRoles, userSelectedGroup, roleArbitrationInstance, userFunctions, transactionFunctions, isSmallView })),
        Certificates: gatewayConfig && _jsx(GatewayCertificatesTab, { gatewayConfig }),
    };
    const { handlers: { handleTabChange }, state: [[tabValue, setTabValue]], } = useTabHandlerStringKeys(tabsToDisplay);
    const urlOverrides = {
        AWS_LPWAN: '/system/IoT Core.svg',
        Chirpstack: '/system/chirpstack.png',
        ThingPark: '/system/thingpark.png',
        Interceptor: undefined,
        Helium: undefined,
        TTN_Community: undefined,
        TTN_Enterprise: '/system/ttn.png',
    };
    const urlOverride = urlOverrides[gateway?.networkKey ?? 'Interceptor'];
    return (_jsxs(_Fragment, { children: [_jsx(ObjectHeader, { vob: gateway ?? matchedPrimary, urlOverride }), _jsxs(TabContext, { value: tabValue, children: [_jsx(TabList, { onChange: handleTabChange, children: Object.entries(tabsToDisplay)
                            .filter(([_, element]) => element !== null)
                            .map(([tabName, _], i) => (_jsx(Tab, { value: tabName, label: toTitleCase(tabName), disabled: false }, `tabHeader_${i}`))) }), Object.entries(tabsToDisplay)
                        .filter(([_, c]) => c !== null)
                        .map(([k, component]) => (_jsx(TabPanel, { value: k, sx: { padding: 0, paddingBlockStart: 1 }, children: component }, `tabBase_${k}`)))] })] }));
};
export default GatewayForm;
